import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

export default function MortgageCalc() {
  const [homePrice, setHomePrice] = useState(350000);
  const [downPayment, setDownPayment] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);
  const [tax, setTax] = useState(3000);
  const [insurance, setInsurance] = useState(1200);

  const result = useMemo(() => {
    const loan = homePrice * (1 - downPayment / 100);
    const monthlyRate = rate / 100 / 12;
    const n = years * 12;
    let monthlyPrincipal;
    if (monthlyRate === 0) {
      monthlyPrincipal = loan / n;
    } else {
      monthlyPrincipal = (loan * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    }
    const monthlyTax = tax / 12;
    const monthlyIns = insurance / 12;
    const total = monthlyPrincipal + monthlyTax + monthlyIns;
    return { loan, monthlyPrincipal, monthlyTax, monthlyIns, total, downPaymentAmt: homePrice * downPayment / 100 };
  }, [homePrice, downPayment, rate, years, tax, insurance]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm" data-testid="mortgage-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-xl font-bold text-stone-900">Mortgage Estimator</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Home Price</Label>
          <Input type="number" value={homePrice} onChange={(e) => setHomePrice(Number(e.target.value))} className="text-stone-900" data-testid="mortgage-price" />
          <Slider value={[homePrice]} onValueChange={(v) => setHomePrice(v[0])} min={100000} max={3000000} step={10000} className="mt-2" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Down Payment (%)</Label>
            <Input type="number" value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))} className="text-stone-900" data-testid="mortgage-down" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Interest Rate (%)</Label>
            <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="text-stone-900" data-testid="mortgage-rate" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Property Tax / Year</Label>
            <Input type="number" value={tax} onChange={(e) => setTax(Number(e.target.value))} className="text-stone-900" data-testid="mortgage-tax" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Insurance / Year</Label>
            <Input type="number" value={insurance} onChange={(e) => setInsurance(Number(e.target.value))} className="text-stone-900" data-testid="mortgage-ins" />
          </div>
        </div>
        <div className="bg-emerald-50 rounded-2xl p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Total Monthly Payment</span>
            <span className="font-outfit font-bold text-emerald-900 text-lg" data-testid="mortgage-total">{fmt(result.total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500 text-xs">Principal & Interest</span>
            <span className="text-stone-600 text-sm">{fmt(result.monthlyPrincipal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500 text-xs">Property Tax</span>
            <span className="text-stone-600 text-sm">{fmt(result.monthlyTax)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500 text-xs">Insurance</span>
            <span className="text-stone-600 text-sm">{fmt(result.monthlyIns)}</span>
          </div>
          <hr className="border-emerald-200" />
          <div className="flex justify-between">
            <span className="text-stone-500 text-xs">Loan Amount</span>
            <span className="text-stone-600 text-sm">{fmt(result.loan)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500 text-xs">Down Payment</span>
            <span className="text-stone-600 text-sm">{fmt(result.downPaymentAmt)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
